import { StyleSheet, css } from 'aphrodite'
import type React from 'react'
import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTagContext } from '../../contexts/TagContext'
import { useUser } from '../../contexts/UserContext'
import {
  getRelatedBrokerName,
  getRelatedContractorName,
} from '../../services/tagService'
import type { Tag } from '../../types/tag'
import { getAssignedPersonName } from '../../utils/relationshipHelpers'
import StatusBadge from '../StatusBadge'
import TruncatableCell from '../TruncatableCell'
import AddTagModal from './AddTagModal'
import ViewTagModal from './ViewTagModal'

interface TagSubRowProps {
  tags: Tag[]
  workOrderId: string
  columnsCount: number
  isReportMode?: boolean
  selectedTags?: Set<string>
  onTagToggle?: (tagId: string, checked: boolean) => void
}

const TagSubRow: React.FC<TagSubRowProps> = ({
  tags,
  workOrderId,
  columnsCount,
  isReportMode = false,
  selectedTags = new Set(),
  onTagToggle,
}) => {
  const navigate = useNavigate()
  const { user: currentUser } = useUser()
  const [editingTag, setLocalEditingTag] = useState<Tag | null>(null)
  const [viewingTag, setViewingTag] = useState<Tag | null>(null)
  const [isExpanded, setIsExpanded] = useState<boolean>(false)
  const [contractorNames, setContractorNames] = useState<
    Record<number, string>
  >({})
  const [brokerNames, setBrokerNames] = useState<Record<number, string>>({})

  // Memoize the expensive filtering operation
  const relatedTags = useMemo(() => {
    return tags.filter((tag) => {
      const tagWorkorderId =
        typeof tag.workorder === 'object' && tag.workorder
          ? (tag.workorder as { id: number }).id
          : tag.workorder
      return String(tagWorkorderId) === workOrderId
    })
  }, [tags, workOrderId])

  // Fetch contractor and broker names for each tag
  useEffect(() => {
    const fetchContractorAndBrokerNames = async () => {
      const newContractorNames: Record<number, string> = {}
      const newBrokerNames: Record<number, string> = {}

      for (const tag of relatedTags) {
        try {
          const [contractorName, brokerName] = await Promise.all([
            getRelatedContractorName(tag),
            getRelatedBrokerName(tag),
          ])
          newContractorNames[tag.id] = contractorName
          newBrokerNames[tag.id] = brokerName
        } catch (error) {
          console.error(`Error fetching names for tag ${tag.id}:`, error)
          newContractorNames[tag.id] = '-'
          newBrokerNames[tag.id] = '-'
        }
      }

      setContractorNames(newContractorNames)
      setBrokerNames(newBrokerNames)
    }

    if (relatedTags.length > 0) {
      fetchContractorAndBrokerNames()
    }
  }, [relatedTags])

  // Early return if no tags or no related tags - avoid rendering empty components
  if (!tags || tags.length === 0 || relatedTags.length === 0) {
    return null
  }

  // Add error check for malformed tag data (but allow for new fields)
  const hasInvalidTags = relatedTags.some(
    (tag) =>
      !tag.assigned_user &&
      !tag.assigned_preuser &&
      (!tag.assigned_to || !tag.assigned_to.contact_object),
  )
  if (hasInvalidTags) {
    console.error('Invalid tag data found:', relatedTags)
    return (
      <tr>
        <td colSpan={columnsCount}>
          <div className={css(styles.errorMessage)}>
            <h3>Error: Invalid Tag Data</h3>
            <p>
              One or more tags are missing required driver information. Please
              contact support.
            </p>
          </div>
        </td>
      </tr>
    )
  }

  // Check if current user can edit this tag
  const canEditTag = (tag: Tag) => {
    if (!currentUser) return false

    const tagCreatorId = tag.created_by_id || tag.created_by?.id
    if (!tagCreatorId) return false

    return currentUser.id === tagCreatorId
  }

  const handleTagAction = (tag: Tag) => {
    const status = tag.status.toUpperCase()

    // Don't do anything if button is disabled
    if (isButtonDisabled(tag)) {
      return
    }

    if (status === 'NEW') {
      if (canEditTag(tag)) {
        setLocalEditingTag(tag)
      } else {
        setViewingTag(tag)
      }
    } else if (status === 'COMPLETE') {
      navigate(`/tags/completed/${tag.id}/confirmation`)
    } else {
      navigate(`/work-orders/tags/${tag.id}`)
    }
  }

  const isButtonDisabled = (tag: Tag) => {
    const status = tag.status.toUpperCase()

    // Always disable for accepted or in progress tags
    if (status === 'ACCEPTED' || status === 'IN_PROGRESS') {
      return true
    }

    // For NEW tags, always enable button (will show edit for creator, view for non-creator)
    if (status === 'NEW') {
      return false
    }

    // For other statuses, use existing logic
    return false
  }

  // Enhanced driver name display with fallback
  const getDriverName = (tag: Tag) => {
    // If we have enhanced API response data, use it
    if (tag.assigned_user || tag.assigned_preuser) {
      return getAssignedPersonName(
        tag.assigned_user,
        tag.assigned_preuser,
        undefined,
      )
    }
    // Fallback to legacy contact display
    return tag.assigned_to?.contact_object?.name || 'Unassigned'
  }

  // Format start date with time for display
  const formatStartDateTime = (startDate?: string) => {
    if (!startDate) return 'Not set'
    try {
      const date = new Date(startDate)
      return date.toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
      })
    } catch (error) {
      console.error('Error formatting start date:', error)
      return 'Invalid date'
    }
  }

  // Get material from tag
  const getMaterial = (tag: Tag) => {
    return tag.material || 'Not specified'
  }

  return (
    <>
      <tr>
        <td colSpan={columnsCount}>
          <div className={css(styles.subComponent)}>
            <div className={css(styles.expandHeader)}>
              <button
                onClick={() => setIsExpanded(!isExpanded)}
                className={css(styles.expandButton)}
                title={isExpanded ? 'Collapse tags' : 'Expand tags'}
              >
                <i
                  className={`fa-solid ${isExpanded ? 'fa-chevron-up' : 'fa-chevron-down'}`}
                />
                <span className={css(styles.expandText)}>
                  Tags ({relatedTags.length})
                </span>
              </button>
            </div>
            {isExpanded && (
              <div className={css(styles.expandableContent)}>
                <div className={css(styles.horizontalScroll)}>
                  {/* Column Headers */}
                  <div
                    className={css(
                      isReportMode
                        ? styles.columnHeadersReportMode
                        : styles.columnHeaders,
                    )}
                  >
                    {isReportMode && (
                      <div
                        className={css(
                          styles.columnHeader,
                          styles.checkboxColumn,
                        )}
                      >
                        {/* Empty header for checkbox column alignment */}
                      </div>
                    )}
                    {!isReportMode && (
                      <div
                        className={css(
                          styles.columnHeader,
                          styles.actionColumn,
                        )}
                      >
                        Action
                      </div>
                    )}
                    <div
                      className={css(
                        styles.columnHeader,
                        styles.startDateColumn,
                      )}
                    >
                      Start Date
                    </div>
                    <div
                      className={css(styles.columnHeader, styles.labelColumn)}
                    >
                      Label
                    </div>
                    <div
                      className={css(styles.columnHeader, styles.titleColumn)}
                    >
                      Title
                    </div>
                    <div
                      className={css(styles.columnHeader, styles.statusColumn)}
                    >
                      Status
                    </div>
                    <div
                      className={css(styles.columnHeader, styles.driverColumn)}
                    >
                      Driver
                    </div>
                    <div
                      className={css(
                        styles.columnHeader,
                        styles.materialColumn,
                      )}
                    >
                      Material
                    </div>
                    <div
                      className={css(
                        styles.columnHeader,
                        styles.contractorColumn,
                      )}
                    >
                      Contractor
                    </div>
                    <div
                      className={css(styles.columnHeader, styles.brokerColumn)}
                    >
                      Broker
                    </div>
                    <div
                      className={css(
                        styles.columnHeader,
                        styles.declineReasonColumn,
                      )}
                    >
                      Decline Reason
                    </div>
                  </div>

                  {/* Tag Rows */}
                  {relatedTags.map((tag) => (
                    <div key={tag.id} className={css(styles.tagRow)}>
                      <div
                        className={css(
                          isReportMode
                            ? styles.tagInfoReportMode
                            : styles.tagInfo,
                        )}
                      >
                        {isReportMode && (
                          <div className={css(styles.checkboxColumn)}>
                            <input
                              type="checkbox"
                              checked={selectedTags.has(tag.id.toString())}
                              onChange={(e) =>
                                onTagToggle?.(
                                  tag.id.toString(),
                                  e.target.checked,
                                )
                              }
                            />
                          </div>
                        )}
                        {!isReportMode && (
                          <div className={css(styles.actionColumn)}>
                            <button
                              onClick={() => handleTagAction(tag)}
                              disabled={isButtonDisabled(tag)}
                              className={css(
                                styles.viewButton,
                                isButtonDisabled(tag) && styles.disabledButton,
                              )}
                            >
                              <i
                                className={`fa-solid ${
                                  tag.status.toUpperCase() === 'NEW'
                                    ? canEditTag(tag)
                                      ? 'fa-pen'
                                      : 'fa-eye'
                                    : 'fa-eye'
                                }`}
                              />
                            </button>
                          </div>
                        )}
                        <div className={css(styles.startDateColumn)}>
                          <TruncatableCell
                            content={formatStartDateTime(tag.start_date)}
                            maxWidth="150px"
                          />
                        </div>
                        <div className={css(styles.labelColumn)}>
                          <span className={css(styles.tagLabel)}>
                            {tag.label}
                          </span>
                        </div>
                        <div className={css(styles.titleColumn)}>
                          <TruncatableCell
                            content={tag.title || '-'}
                            maxWidth="120px"
                          />
                        </div>
                        <div className={css(styles.statusColumn)}>
                          <StatusBadge status={tag.status} />
                        </div>
                        <div className={css(styles.driverColumn)}>
                          <TruncatableCell
                            content={getDriverName(tag)}
                            maxWidth="120px"
                          />
                        </div>
                        <div className={css(styles.materialColumn)}>
                          <TruncatableCell
                            content={getMaterial(tag)}
                            maxWidth="120px"
                          />
                        </div>
                        <div className={css(styles.contractorColumn)}>
                          <TruncatableCell
                            content={contractorNames[tag.id] || 'Loading...'}
                            maxWidth="120px"
                          />
                        </div>
                        <div className={css(styles.brokerColumn)}>
                          <TruncatableCell
                            content={brokerNames[tag.id] || 'Loading...'}
                            maxWidth="120px"
                          />
                        </div>
                        <div className={css(styles.declineReasonColumn)}>
                          <TruncatableCell
                            content={
                              tag.decline_reason
                                ? tag.decline_reason.length > 50
                                  ? `${tag.decline_reason.substring(0, 50)}...`
                                  : tag.decline_reason
                                : '-'
                            }
                            maxWidth="120px"
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </td>
      </tr>

      {editingTag && (
        <AddTagModal
          workorder={{ id: String(workOrderId) }}
          onClose={() => setLocalEditingTag(null)}
          editingTag={editingTag}
        />
      )}

      {viewingTag && (
        <ViewTagModal tag={viewingTag} onClose={() => setViewingTag(null)} />
      )}
    </>
  )
}

const styles = StyleSheet.create({
  subComponent: {
    padding: '8px 0px 8px 40px',
    backgroundColor: '#F9FAFB',
    width: '100%',
  },
  horizontalScroll: {
    overflowX: 'auto',
    width: '100%',
    paddingBottom: 8,
  },
  tagRow: {
    padding: '8px 12px',
    borderLeft: '2px solid #1C64F2',
    marginBottom: '4px',
    backgroundColor: 'white',
    borderRadius: '4px',
    boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)',
  },
  tagInfo: {
    display: 'grid',
    gridTemplateColumns:
      '60px 150px 120px 80px 150px 120px 120px 120px 120px 120px',
    alignItems: 'center',
    fontSize: '13px',
    gap: '8px',
    width: '100%',
    minWidth: 1108,
    '@media (max-width: 1024px)': {
      gridTemplateColumns:
        '50px 140px 100px 80px 140px 120px 120px 120px 120px 120px',
      fontSize: '12px',
      gap: '6px',
      minWidth: 1028,
    },
  },
  tagInfoReportMode: {
    display: 'grid',
    gridTemplateColumns:
      '50px 150px 120px 80px 150px 120px 120px 120px 120px 120px',
    alignItems: 'center',
    fontSize: '13px',
    gap: '8px',
    width: '100%',
    minWidth: 1088,
    '@media (max-width: 1024px)': {
      gridTemplateColumns:
        '40px 140px 100px 80px 140px 120px 120px 120px 120px 120px',
      fontSize: '12px',
      gap: '6px',
      minWidth: 1008,
    },
  },
  viewButton: {
    padding: '4px 8px',
    borderRadius: '4px',
    border: '1px solid #E5E7EB',
    backgroundColor: 'white',
    cursor: 'pointer',
    color: '#6B7280',
    width: '40px',
    minWidth: '40px',
    display: 'flex',
    justifyContent: 'center',
    ':hover': {
      backgroundColor: '#F3F4F6',
      color: '#1C64F2',
    },
  },
  disabledButton: {
    backgroundColor: '#F9FAFB',
    color: '#D1D5DB',
    cursor: 'not-allowed',
    border: '1px solid #E5E7EB',
    ':hover': {
      backgroundColor: '#F9FAFB',
      color: '#D1D5DB',
    },
  },
  // Column Headers
  columnHeaders: {
    display: 'grid',
    gridTemplateColumns:
      '60px 150px 120px 80px 150px 120px 120px 120px 120px 120px',
    alignItems: 'center',
    fontSize: '12px',
    fontWeight: 600,
    color: '#374151',
    padding: '8px 12px',
    backgroundColor: '#F3F4F6',
    borderBottom: '1px solid #E5E7EB',
    gap: '8px',
    width: '100%',
    minWidth: 1108,
    '@media (max-width: 1024px)': {
      gridTemplateColumns:
        '50px 140px 100px 80px 140px 120px 120px 120px 120px 120px',
      minWidth: 1028,
    },
  },
  columnHeadersReportMode: {
    display: 'grid',
    gridTemplateColumns:
      '50px 150px 120px 80px 150px 120px 120px 120px 120px 120px',
    alignItems: 'center',
    fontSize: '12px',
    fontWeight: 600,
    color: '#374151',
    padding: '8px 12px',
    backgroundColor: '#F3F4F6',
    borderBottom: '1px solid #E5E7EB',
    gap: '8px',
    width: '100%',
    minWidth: 1088,
    '@media (max-width: 1024px)': {
      gridTemplateColumns:
        '40px 140px 100px 80px 140px 120px 120px 120px 120px 120px',
      minWidth: 1008,
    },
  },
  columnHeader: {
    textAlign: 'left',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },

  // Column Layout
  checkboxColumn: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minWidth: '50px',
  },
  actionColumn: {
    display: 'flex',
    justifyContent: 'flex-start',
  },
  labelColumn: {
    display: 'flex',
    justifyContent: 'flex-start',
  },
  titleColumn: {
    display: 'flex',
    justifyContent: 'flex-start',
  },
  statusColumn: {
    display: 'flex',
    justifyContent: 'flex-start',
  },
  driverColumn: {
    display: 'flex',
    justifyContent: 'flex-start',
  },
  materialColumn: {
    display: 'flex',
    justifyContent: 'flex-start',
  },
  contractorColumn: {
    display: 'flex',
    justifyContent: 'flex-start',
  },
  brokerColumn: {
    display: 'flex',
    justifyContent: 'flex-start',
  },
  declineReasonColumn: {
    display: 'flex',
    justifyContent: 'flex-start',
  },
  startDateColumn: {
    display: 'flex',
    justifyContent: 'flex-start',
  },

  // Content Styles
  tagLabel: {
    fontWeight: 600,
    color: '#1C64F2',
    textAlign: 'left',
  },
  tagTitle: {
    color: '#374151',
    textAlign: 'left',
  },
  columnContent: {
    color: '#6B7280',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    minWidth: '0',
  },
  expandHeader: {
    padding: '8px 16px',
    backgroundColor: '#F9FAFB',
  },
  expandButton: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    color: '#6B7280',
    fontSize: '13px',
    fontWeight: 500,
    padding: '4px 0',
    ':hover': {
      color: '#1C64F2',
    },
  },
  expandText: {
    fontSize: '13px',
    fontWeight: 500,
  },
  expandableContent: {
    paddingTop: '8px',
  },
  errorMessage: {
    padding: '16px',
    backgroundColor: '#FEF2F2',
    border: '1px solid #FEE2E2',
    borderRadius: '6px',
    color: '#991B1B',
    margin: '8px 40px',
    '& h3': {
      margin: '0 0 8px 0',
      fontSize: '14px',
      fontWeight: 600,
    },
    '& p': {
      margin: 0,
      fontSize: '13px',
    },
  },
})

export default TagSubRow
